import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, Share2, Mail, Twitter, Facebook, Linkedin, ArrowRight } from 'lucide-react';

const BetaThankYou = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const email = searchParams.get('email') || '';
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    // Success animation trigger
    const timer = setTimeout(() => {
      // Any animation triggers can go here
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  const shareUrl = 'https://bissa.ai/beta';
  const shareText = 'I just signed up for Bissa.ai beta testing! A CRM that builds relationships like the Bissa people of Burkina Faso. Join me!';

  const handleShare = async (platform) => {
    const urls = {
      twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`,
      email: `mailto:?subject=${encodeURIComponent('Check out Bissa.ai Beta')}&body=${encodeURIComponent(`${shareText}\n\n${shareUrl}`)}`
    };

    if (platform === 'email') {
      window.location.href = urls.email;
    } else {
      window.open(urls[platform], '_blank', 'width=600,height=400');
    }
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-bissa-blue to-bissa-blue-700 flex items-center justify-center px-6 py-12">
      <div className="max-w-2xl w-full">
        {/* Success Animation */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-24 h-24 bg-white rounded-full mb-6 animate-bounce">
            <CheckCircle className="w-16 h-16 text-green-500" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Thank You!
          </h1>
          <p className="text-xl text-blue-100 mb-2">
            Your beta application has been received
          </p>
          {email && (
            <p className="text-lg text-blue-200">
              Confirmation sent to <span className="font-semibold">{email}</span>
            </p>
          )}
        </div>

        {/* Main Card */}
        <div className="bg-white rounded-2xl shadow-2xl p-8 md:p-12">
          {/* Timeline */}
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-bissa-blue mb-6">What Happens Next?</h2>
            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-10 h-10 bg-bissa-blue rounded-full flex items-center justify-center text-white font-bold">
                  1
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">Confirmation Email</h3>
                  <p className="text-gray-600">Check your inbox for a confirmation email with next steps.</p>
                </div>
              </div>
              
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-10 h-10 bg-bissa-blue rounded-full flex items-center justify-center text-white font-bold">
                  2
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">Review Process</h3>
                  <p className="text-gray-600">We'll review your application and get back to you within <strong>48 hours</strong>.</p>
                </div>
              </div>
              
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-10 h-10 bg-bissa-blue rounded-full flex items-center justify-center text-white font-bold">
                  3
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">Beta Access</h3>
                  <p className="text-gray-600">Once approved, you'll receive your beta access credentials and onboarding guide.</p>
                </div>
              </div>
            </div>
          </div>

          {/* Social Share */}
          <div className="border-t border-gray-200 pt-8 mb-8">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Share the Beta Program</h3>
            <p className="text-gray-600 mb-6">
              Know someone who would benefit from Bissa.ai? Share the beta program with them!
            </p>
            
            <div className="flex flex-wrap gap-3">
              <button
                onClick={() => handleShare('twitter')}
                className="flex items-center gap-2 px-4 py-2 bg-[#1DA1F2] text-white rounded-lg hover:bg-[#1a8cd8] transition-all"
              >
                <Twitter className="w-4 h-4" />
                Twitter
              </button>
              
              <button
                onClick={() => handleShare('facebook')}
                className="flex items-center gap-2 px-4 py-2 bg-[#1877F2] text-white rounded-lg hover:bg-[#166fe5] transition-all"
              >
                <Facebook className="w-4 h-4" />
                Facebook
              </button>
              
              <button
                onClick={() => handleShare('linkedin')}
                className="flex items-center gap-2 px-4 py-2 bg-[#0A66C2] text-white rounded-lg hover:bg-[#095195] transition-all"
              >
                <Linkedin className="w-4 h-4" />
                LinkedIn
              </button>
              
              <button
                onClick={() => handleShare('email')}
                className="flex items-center gap-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-all"
              >
                <Mail className="w-4 h-4" />
                Email
              </button>
              
              <button
                onClick={handleCopyLink}
                className="flex items-center gap-2 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-all"
              >
                <Share2 className="w-4 h-4" />
                {copied ? 'Copied!' : 'Copy Link'}
              </button>
            </div>
          </div>

          {/* Community Link */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-bissa-blue mb-2">Join Our Community</h3>
            <p className="text-gray-700 mb-4">
              Connect with other beta testers, share feedback, and stay updated on the latest features.
            </p>
            <button
              onClick={() => window.open('https://facebook.com/groups/bissa-ai-beta', '_blank')}
              className="inline-flex items-center gap-2 px-6 py-3 bg-bissa-blue text-white rounded-lg hover:bg-bissa-blue-700 transition-all font-medium"
            >
              Join Waitlist Community
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 mt-8 pt-8 border-t border-gray-200">
            <button
              onClick={() => navigate('/')}
              className="flex-1 px-6 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-all font-medium"
            >
              Back to Home
            </button>
            <button
              onClick={() => navigate('/beta')}
              className="flex-1 px-6 py-3 bg-bissa-gold text-bissa-blue rounded-lg hover:bg-yellow-400 transition-all font-medium"
            >
              Submit Another Application
            </button>
          </div>
        </div>

        {/* Footer Note */}
        <p className="text-center text-sm text-blue-200 mt-6">
          Questions? Contact us at <a href="mailto:support@bissa.ai" className="underline hover:text-white">support@bissa.ai</a>
        </p>
      </div>
    </div>
  );
};

export default BetaThankYou;

